import type { GrowthCategory, Product } from '@mc/api'
import { PurchaseClickEventProps } from '@mc/pre-paywall-events'
import {
  TRACK_PLAYBACK_EVENTS as CORE_TRACK_PLAYBACK_EVENTS,
  EventTypes as CoreEventTypes,
  createSessionId,
  identify,
  track,
  TRACK_PROPS,
  trackGoldStandardEvent,
  trackMedia,
} from '@mc/track-event'
import {
  ActionTypes,
  FUNNEL_STATE_ID,
  KeyTypes,
  saveUserData,
  getTrackedId,
  categoryAccessor,
  subcategoryAccessor,
} from '@mc/user-data'
import type { Course } from 'types/bridgeTypes'

export const trackPurchaseClick = ({
  email,
  action = 'primary',
  location = 'hero_email_capture',
  href,
  pageSource,
}: Partial<PurchaseClickEventProps & { email: string }> = {}) => {
  track(EventTypes.PURCHASE_CLICK, {
    content_type: 'product',
    action,
    location,
    class: 'annual-pass',
    active_annual_pass: false,
    href,
    page_source: pageSource,
  })
  identify(null, { email })
}

type TrackProductViewedCourseProps = {
  annualPass: Product
  course: Course
  coursePrimaryCategory: GrowthCategory | null
  courseCategoryIds: Array<number>
  primarySubcategory: GrowthCategory | null
  courseSubcategoriesIds: Array<number>
}

export const trackProductViewedCourse = ({
  annualPass,
  course,
  coursePrimaryCategory,
  courseCategoryIds,
  primarySubcategory,
  courseSubcategoriesIds,
}: TrackProductViewedCourseProps) => {
  const name = `${course.instructorName} ${course.headline}`

  const courseCatalogId = getTrackedId({
    key: KeyTypes.CLASS,
    id: course.id as number,
  })
  const categoryCatalogId = getTrackedId({
    key: KeyTypes.CATEGORY,
    id: coursePrimaryCategory?.id as number,
  })
  const subcategoryCatalogId = getTrackedId({
    key: KeyTypes.SUBCATEGORY,
    id: primarySubcategory?.id as number,
  })

  const categoryCatalogIds = courseCategoryIds.map((categoryId) =>
    getTrackedId({ key: KeyTypes.CATEGORY, id: categoryId }),
  )

  const subcategoryCatalogIds = courseSubcategoriesIds.map((subcategoryId) =>
    getTrackedId({ key: KeyTypes.SUBCATEGORY, id: subcategoryId }),
  )

  const eventDetails = {
    content_type: 'product',
    name,
    product_id: courseCatalogId,
    price: annualPass.productLtv?.regular,
    quantity: 1,
    category: categoryCatalogId,
    category_ids: categoryCatalogIds,
    subcategory_ids: subcategoryCatalogIds,
    stored_category_id: categoryCatalogId,
    stored_subcategory_id: subcategoryCatalogId,
    stored_course_id: courseCatalogId,
    currency: annualPass?.pricing?.currency,
  }

  trackGoldStandardEvent(
    CoreEventTypes.PRODUCT_VIEWED_GOLD_STANDARD,
    eventDetails,
  )

  categoryAccessor.setBrowsedInterestIds(
    courseCategoryIds ?? [],
    CoreEventTypes.PRODUCT_VIEWED_GOLD_STANDARD,
  )

  subcategoryAccessor.setBrowsedInterestIds(
    courseSubcategoriesIds,
    CoreEventTypes.PRODUCT_VIEWED_GOLD_STANDARD,
  )

  saveUserData({
    key: KeyTypes.CLASS,
    id: courseCatalogId,
    actionType: ActionTypes.BROWSING,
    eventName: CoreEventTypes.PRODUCT_VIEWED_GOLD_STANDARD,
  })
}

export const trackProductViewed = (
  annualPass: Product,
  name = 'Annual Membership',
) => {
  const eventDetails = {
    content_type: 'product',
    name,
    product_id: annualPass.id,
    price: annualPass?.productLtv?.regular,
    quantity: 1,
    category: '0',
    currency: annualPass?.pricing?.currency,
  }
  trackGoldStandardEvent(
    CoreEventTypes.PRODUCT_VIEWED_GOLD_STANDARD,
    eventDetails,
  )
}

export const trackProductListViewed = (categoryId: number) => {
  const catId = getTrackedId({ key: KeyTypes.CATEGORY, id: categoryId })

  trackGoldStandardEvent(CoreEventTypes.PRODUCT_LIST_VIEWED_GOLD_STANDARD, {
    category: categoryId ? catId : '0',
    ...(categoryId && { products: [{ product_id: catId }] }),
    stored_category_id: catId,
  })

  if (categoryId) {
    saveUserData({
      key: KeyTypes.CATEGORY,
      id: catId,
      actionType: ActionTypes.BROWSING,
      eventName: CoreEventTypes.PRODUCT_LIST_VIEWED_GOLD_STANDARD,
    })
  }
}

export const trackQuizPageViewed = (props: Record<string, unknown>) => {
  trackGoldStandardEvent(CoreEventTypes.QUIZ_PAGE_VIEWED, props)
  saveUserData({
    key: KeyTypes.FUNNEL_STATE,
    id: FUNNEL_STATE_ID,
    actionType: ActionTypes.USER_STARTED_QUIZ,
    eventName: CoreEventTypes.QUIZ_PAGE_VIEWED,
  })
}

export const trackMediaPlayback = (eventName: string, extraInfo = {}) => {
  const { PLAYER_SESSION_ID, MEDIA_SESSION_ID } = TRACK_PROPS

  trackMedia(eventName, {
    [PLAYER_SESSION_ID]: createSessionId(),
    [MEDIA_SESSION_ID]: createSessionId(),
    ...extraInfo,
  })
}

export const EventTypes = CoreEventTypes

export const TRACK_PLAYBACK_EVENTS = CORE_TRACK_PLAYBACK_EVENTS
